import { FC, Fragment, useState, useEffect, useRef } from "react";
import { useJsApiLoader } from "@react-google-maps/api";

const libraries: ("places")[] = ["places"];

interface Props {
  value: string;
  onChange: (address: google.maps.places.PlaceResult) => void;
}

interface Prediction {
  description: string;
  place_id: string;
}

const PlacesAutocomplete: FC<Props> = ({ value, onChange }) => {
  const [address, setAddress] = useState<string>(value);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const userSelectedRef = useRef<boolean>(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API,
    libraries,
  });

  useEffect(() => {
    // Only update from parent if user hasn't manually selected an address
    if (!userSelectedRef.current) {
      setAddress(value);
    }
  }, [value]);

  useEffect(() => {
    if (isLoaded) {
      autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
      const dummyDiv = document.createElement("div");
      placesServiceRef.current = new google.maps.places.PlacesService(dummyDiv);
    }
  }, [isLoaded]);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowPredictions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setAddress(inputValue);
    
    // Reset the flag when user manually types
    userSelectedRef.current = false;

    if (inputValue.length > 2 && autocompleteServiceRef.current) {
      // Get predictions
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: inputValue,
          componentRestrictions: { country: "us" },
          types: ["address"],
        },
        (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            setPredictions(
              predictions.map((p) => ({
                description: p.description,
                place_id: p.place_id,
              }))
            );
            setShowPredictions(true);
          } else {
            setPredictions([]);
            setShowPredictions(false);
          }
        }
      );
    } else {
      setPredictions([]);
      setShowPredictions(false);
    }
  };

  const handlePredictionClick = (prediction: Prediction) => {
    // Set the full description (what user sees in dropdown) as the input value
    const selectedDescription = prediction.description;
    setAddress(selectedDescription);
    setShowPredictions(false);
    setPredictions([]);
    
    // Mark that user has selected an address to prevent parent updates
    userSelectedRef.current = true;

    // Get place details with lat/lng
    if (placesServiceRef.current) {
      placesServiceRef.current.getDetails(
        {
          placeId: prediction.place_id,
          fields: ["formatted_address", "geometry", "place_id", "address_components", "name"],
        },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            // Add the original full description to the place object
            const placeWithDescription = {
              ...place,
              description: selectedDescription
            };
            // Call onChange with the enhanced place details
            onChange(placeWithDescription);
          }
        }
      );
    }
  };

  if (!isLoaded) return <div className="mt-3">Loading...</div>;

  return (
    <Fragment>
      <div ref={wrapperRef} className="relative w-full">
        <input
          type="text"
          placeholder="Enter your business address"
          value={address}
          onChange={handleInputChange}
          className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
        />
        {showPredictions && predictions.length > 0 && (
          <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
            {predictions.map((prediction) => (
              <div
                key={prediction.place_id}
                onClick={() => handlePredictionClick(prediction)}
                className="px-4 py-3 hover:bg-gray-100 cursor-pointer flex items-start gap-2 border-b last:border-b-0"
              >
                <svg
                  className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm text-gray-700">{prediction.description}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default PlacesAutocomplete;